import React, {useEffect, useState} from "react";
import {Chart} from "react-google-charts";
import moment from "moment";

const DrawMeasureChart = (props) => {
    const [seriesColor, setSeriesColor] = useState({
        0: { color: '#4285F4', lineWidth: 2,spanGaps:true,type:'line',targetAxisIndex: 0, },
        1: { color: '#EA4335', lineWidth: 2 ,spanGaps:true,type:'line',ignoreEmptyValue: true},
        2: { color: '#FBBC04', lineWidth: 2,spanGaps:true,type:'line',targetAxisIndex: 2},
        3: { color: '#B71C1C', visibleInLegend: false, lineWidth: 1 ,spanGaps:true,type:'line',targetAxisIndex: 3},
        4: { color: '#B71C1C', visibleInLegend: false, lineWidth: 1 ,spanGaps:true,type:'line',targetAxisIndex: 4},
    })

    const dateFormatter = (date) => {
        const format = 'YYYY/MM/DD HH:mm';
        if (!date) return '';
        if (date.toDate && typeof date.toDate === 'function') {
            return moment(date.toDate()).format(format);
        }
        if (date.seconds !== undefined) {
            return moment(new Date(date.seconds * 1000)).format(format);
        }
        if (moment(date).isValid()) {
            return moment(date).format(format);
        }
        return '';
    }

    const escapeHtml = (str) => {
        if (str === null || str === undefined) return '';
        return String(str)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#039;');
    };

    const createTooltipHtml = (dateStr, label, value, comment) => {
        const valText = (value !== null && value !== undefined && !isNaN(value)) ? value : '-';
        let html = `<div style="padding: 8px 10px; font-size: 12px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; min-width: 150px; line-height: 1.4; color: #212529;">`;
        html += `<div style="font-weight: 600; color: #495057; border-bottom: 1px solid #e9ecef; padding-bottom: 3px; margin-bottom: 5px;">採取日: ${escapeHtml(dateStr)}</div>`;
        html += `<div style="margin-bottom: 3px;">${escapeHtml(label)}: <span style="font-weight: 700; color: #000;">${escapeHtml(valText)}</span></div>`;
        
        if (comment && String(comment).trim() !== '') {
            const commentLines = escapeHtml(comment).split('\n').join('<br/>');
            html += `<div style="margin-top: 5px; background-color: #e8f4f8; border-left: 3px solid #17a2b8; padding: 4px 6px; border-radius: 3px; font-size: 11px; color: #0c5460;">`;
            html += `<div style="font-weight: 700; margin-bottom: 2px;">💬 コメント:</div>`;
            html += `<div style="white-space: pre-wrap; word-break: break-word;">${commentLines}</div>`;
            html += `</div>`;
        }
        html += `</div>`;
        return html;
    };

    const buildCommentMap = (comments) => {
        const map = {};
        if (Array.isArray(comments)) {
            comments.forEach(c => {
                if (c && c.insertedDate && c.comment && String(c.comment).trim() !== '') {
                    const fullDate = dateFormatter(c.insertedDate);
                    if (fullDate && fullDate.length >= 16) {
                        const shortDate = fullDate.substring(5, 16);
                        const text = String(c.comment).trim();
                        
                        if (map[fullDate]) {
                            map[fullDate] += '\n' + text;
                        } else {
                            map[fullDate] = text;
                        }
                        
                        if (map[shortDate]) {
                            map[shortDate] += '\n' + text;
                        } else {
                            map[shortDate] = text;
                        }
                    }
                }
            });
        }
        return map;
    };

    const formatBegin = (item) => {
        // Extract data
        const fieldData = getDataArray(item.fieldMeasure, 'field');
        const labData = getDataArray(item.labMeasure, 'lab');
        const automaticData = getDataArray(item.automaticMeasure, 'automatic');

        const processData = props.getValues ? props.getValues(`process[${props.nestIndex}]`) : {};
        const fieldComments = (processData && processData.fieldComments) || (item && item.fieldComments) || [];
        const labComments = (processData && processData.labComments) || (item && item.labComments) || [];
        const automaticComments = (processData && processData.automaticComments) || (item && item.automaticComments) || [];

        const fieldCommentMap = buildCommentMap(fieldComments);
        const labCommentMap = buildCommentMap(labComments);
        const autoCommentMap = buildCommentMap(automaticComments);

        let result;
        switch (props.which) {
            case 'none':
                result = mergeData(fieldData, labData, automaticData, item.tolerance_lvl_max, item.tolerance_lvl_min, item.range_settings, fieldCommentMap, labCommentMap, autoCommentMap);
                break;
            case 'preview':
                result = mergeData(fieldData, labData, automaticData, item.tolerance_lvl_max, item.tolerance_lvl_min, item.range_settings, fieldCommentMap, labCommentMap, autoCommentMap);
                break;
            case 'field':
                result = mergeDataField(fieldData, item.tolerance_lvl_max, item.tolerance_lvl_min, item.range_settings, fieldCommentMap);
                break;
            case 'lab':
                result = mergeDataLab(labData, item.tolerance_lvl_max, item.tolerance_lvl_min, item.range_settings, labCommentMap);
                break;
            case 'automatic':
                result = mergeDataAuto(automaticData, item.tolerance_lvl_max, item.tolerance_lvl_min, item.range_settings, autoCommentMap);
                break;
            default:
                result = mergeData(fieldData, labData, automaticData, item.tolerance_lvl_max, item.tolerance_lvl_min, item.range_settings, fieldCommentMap, labCommentMap, autoCommentMap);
                break;
        }
        return result;
    }

    useEffect(() => {
        switch (props.which) {
            case 'none':
                setSeriesColor({
                    0: { color: '#4285F4', lineWidth: 2,spanGaps:true,type:'line'},
                    1: { color: '#EA4335', lineWidth: 2,spanGaps:true,type:'line',ignoreEmptyValue: true},
                    2: { color: '#FBBC04', lineWidth: 2,spanGaps:true,type:'line'},
                    3: { color: '#B71C1C', visibleInLegend: false, lineWidth: 1,spanGaps:true,type:'line'},
                    4: { color: '#B71C1C', visibleInLegend: false, lineWidth: 1,spanGaps:true,type:'line'},
                });
                break;
            case 'field' :
                setSeriesColor({
                    0: { color: '#4285F4', lineWidth: 2 },
                    1: { color: '#B71C1C', visibleInLegend: false, lineWidth: 1 },
                    2: { color: '#B71C1C', visibleInLegend: false, lineWidth: 1 },
                });
                break;
            case 'lab' :
                setSeriesColor({
                    0: { color: '#EA4335', lineWidth: 2 },
                    1: { color: '#B71C1C', visibleInLegend: false, lineWidth: 1 },
                    2: { color: '#B71C1C', visibleInLegend: false, lineWidth: 1 },
                });
                break;
            case 'automatic':
                setSeriesColor({
                    0: { color: '#FBBC04', lineWidth: 2 },
                    1: { color: '#B71C1C', visibleInLegend: false, lineWidth: 1 },
                    2: { color: '#B71C1C', visibleInLegend: false, lineWidth: 1 },
                });
                break;
            default:
                setSeriesColor({
                    0: { color: '#4285F4', lineWidth: 2 },
                    1: { color: '#EA4335', lineWidth: 2 },
                    2: { color: '#FBBC04', lineWidth: 2 },
                    3: { color: '#B71C1C', visibleInLegend: false, lineWidth: 1 },
                    4: { color: '#B71C1C', visibleInLegend: false, lineWidth: 1 },
                });
                break;
        }
    }, [props.which])

    const compareDates = (a, b)=> {
        const dateA = a[0] === "insertedDate" ? '1900/00/00 00:00' : a[0];
        const dateB = b[0] === "insertedDate" ? '1900/00/00 00:00' : b[0];

        if (dateA < dateB) {
            return -1;
        }
        if (dateA > dateB) {
            return 1;
        }
        return 0;
    }

    const getDataArray = (measurements, label) => {
        const dataArray = [['1900/00/00 00:00', label]];
        if (measurements && measurements.length > 0) {
            measurements.forEach(measurement => {
                if (measurement && measurement.insertedDate) {
                    const date = dateFormatter(measurement.insertedDate);
                    if (date) {
                        dataArray.push([date, parseFloat(measurement.value)]);
                    }
                }
            });
        }
        dataArray.sort(compareDates);
        return dataArray;
    }

    // Function to merge data arrays none all
    const mergeData = (fieldData, labData, automaticData, mx, mn, range, fieldCommentMap, labCommentMap, autoCommentMap) => {
        const mergedData = [
            [
                'insertedDate',
                { label: '現地測定値', type: 'number' },
                { type: 'string', role: 'tooltip', p: { html: true } },
                { label: 'ラボ測定値', type: 'number' },
                { type: 'string', role: 'tooltip', p: { html: true } },
                { label: '自動測定値', type: 'number' },
                { type: 'string', role: 'tooltip', p: { html: true } },
                { label: '以下', type: 'number' },
                { label: '以上', type: 'number' }
            ]
        ];

        const allDates = new Set([
            ...fieldData.slice(1).map(row => row[0]),
            ...labData.slice(1).map(row => row[0]),
            ...automaticData.slice(1).map(row => row[0]),
            ...Object.keys(fieldCommentMap).filter(d => d.length >= 16),
            ...Object.keys(labCommentMap).filter(d => d.length >= 16),
            ...Object.keys(autoCommentMap).filter(d => d.length >= 16)
        ]);

        let max = null;
        let min = null;
        switch (range) {
            case '上限下限共':
                max = mx * 1;
                min = mn * 1;
                break;
            case '下限のみ':
                max = null;
                min = mn * 1;
                break;
            case '上限のみ':
                max = mx * 1;
                min = null;
                break;
            default:
                max = mx * 1;
                min = mn * 1;
                break;
        }

        const sortedDates = Array.from(allDates).sort((a, b) => {
            const dateA = a === "insertedDate" ? '1900/00/00 00:00' : a;
            const dateB = b === "insertedDate" ? '1900/00/00 00:00' : b;
            return dateA < dateB ? -1 : (dateA > dateB ? 1 : 0);
        });

        sortedDates.forEach(date => {
            const fieldValue = getFieldDataForDate(date, fieldData);
            const labValue = getFieldDataForDate(date, labData);
            const automaticValue = getFieldDataForDate(date, automaticData);

            if (!isNaN(fieldValue) || !isNaN(labValue) || !isNaN(automaticValue)) {
                const shortDate = date.length >= 16 ? date.substring(5, 16) : date;
                const fComment = fieldCommentMap[date] || fieldCommentMap[shortDate];
                const lComment = labCommentMap[date] || labCommentMap[shortDate];
                const aComment = autoCommentMap[date] || autoCommentMap[shortDate];

                const fieldTooltip = createTooltipHtml(shortDate, '現地測定値', fieldValue, fComment);
                const labTooltip = createTooltipHtml(shortDate, 'ラボ測定値', labValue, lComment);
                const autoTooltip = createTooltipHtml(shortDate, '自動測定値', automaticValue, aComment);

                mergedData.push([shortDate, fieldValue, fieldTooltip, labValue, labTooltip, automaticValue, autoTooltip, max, min]);
            }
        });

        return mergedData;
    }

    const mergeDataField = (fieldData, mx, mn, range, fieldCommentMap) => {
        const mergedData = [
            [
                'insertedDate',
                { label: '現地測定値', type: 'number' },
                { type: 'string', role: 'tooltip', p: { html: true } },
                { label: '以下', type: 'number' },
                { label: '以上', type: 'number' }
            ]
        ];

        const allDates = new Set([
            ...fieldData.slice(1).map(row => row[0]),
            ...Object.keys(fieldCommentMap).filter(d => d.length >= 16)
        ]);

        let max = null;
        let min = null;
        switch (range) {
            case '上限下限共':
                max = mx * 1;
                min = mn * 1;
                break;
            case '下限のみ':
                max = null;
                min = mn * 1;
                break;
            case '上限のみ':
                max = mx * 1;
                min = null;
                break;
            default:
                max = mx * 1;
                min = mn * 1;
                break;
        }

        const sortedDates = Array.from(allDates).sort((a, b) => {
            const dateA = a === "insertedDate" ? '1900/00/00 00:00' : a;
            const dateB = b === "insertedDate" ? '1900/00/00 00:00' : b;
            return dateA < dateB ? -1 : (dateA > dateB ? 1 : 0);
        });

        sortedDates.forEach(date => {
            const fieldValue = getFieldDataForDate(date, fieldData);
            if (!isNaN(fieldValue)) {
                const shortDate = date.length >= 16 ? date.substring(5, 16) : date;
                const fComment = fieldCommentMap[date] || fieldCommentMap[shortDate];
                const fieldTooltip = createTooltipHtml(shortDate, '現地測定値', fieldValue, fComment);

                mergedData.push([shortDate, fieldValue, fieldTooltip, max, min]);
            }
        });

        return mergedData;
    }

    const mergeDataLab = (labData, mx, mn, range, labCommentMap) => {
        const mergedData = [
            [
                'insertedDate',
                { label: 'ラボ測定値', type: 'number' },
                { type: 'string', role: 'tooltip', p: { html: true } },
                { label: '以下', type: 'number' },
                { label: '以上', type: 'number' }
            ]
        ];

        const allDates = new Set([
            ...labData.slice(1).map(row => row[0]),
            ...Object.keys(labCommentMap).filter(d => d.length >= 16)
        ]);

        let max = null;
        let min = null;
        switch (range) {
            case '上限下限共':
                max = mx * 1;
                min = mn * 1;
                break;
            case '下限のみ':
                max = null;
                min = mn * 1;
                break;
            case '上限のみ':
                max = mx * 1;
                min = null;
                break;
            default:
                max = mx * 1;
                min = mn * 1;
                break;
        }

        const sortedDates = Array.from(allDates).sort((a, b) => {
            const dateA = a === "insertedDate" ? '1900/00/00 00:00' : a;
            const dateB = b === "insertedDate" ? '1900/00/00 00:00' : b;
            return dateA < dateB ? -1 : (dateA > dateB ? 1 : 0);
        });

        sortedDates.forEach(date => {
            const labValue = getFieldDataForDate(date, labData);
            if (!isNaN(labValue)) {
                const shortDate = date.length >= 16 ? date.substring(5, 16) : date;
                const lComment = labCommentMap[date] || labCommentMap[shortDate];
                const labTooltip = createTooltipHtml(shortDate, 'ラボ測定値', labValue, lComment);

                mergedData.push([shortDate, labValue, labTooltip, max, min]);
            }
        });

        return mergedData;
    }

    const mergeDataAuto = (automaticData, mx, mn, range, autoCommentMap) => {
        const mergedData = [
            [
                'insertedDate',
                { label: '自動測定値', type: 'number' },
                { type: 'string', role: 'tooltip', p: { html: true } },
                { label: '以下', type: 'number' },
                { label: '以上', type: 'number' }
            ]
        ];

        const allDates = new Set([
            ...automaticData.slice(1).map(row => row[0]),
            ...Object.keys(autoCommentMap).filter(d => d.length >= 16)
        ]);

        let max = null;
        let min = null;
        switch (range) {
            case '上限下限共':
                max = mx * 1;
                min = mn * 1;
                break;
            case '下限のみ':
                max = null;
                min = mn * 1;
                break;
            case '上限のみ':
                max = mx * 1;
                min = null;
                break;
            default:
                max = mx * 1;
                min = mn * 1;
                break;
        }

        const sortedDates = Array.from(allDates).sort((a, b) => {
            const dateA = a === "insertedDate" ? '1900/00/00 00:00' : a;
            const dateB = b === "insertedDate" ? '1900/00/00 00:00' : b;
            return dateA < dateB ? -1 : (dateA > dateB ? 1 : 0);
        });

        sortedDates.forEach(date => {
            const automaticValue = getFieldDataForDate(date, automaticData);
            if (!isNaN(automaticValue)) {
                const shortDate = date.length >= 16 ? date.substring(5, 16) : date;
                const aComment = autoCommentMap[date] || autoCommentMap[shortDate];
                const autoTooltip = createTooltipHtml(shortDate, '自動測定値', automaticValue, aComment);

                mergedData.push([shortDate, automaticValue, autoTooltip, max, min]);
            }
        });

        return mergedData;
    }

    // Function to get field data for a specific date
    const getFieldDataForDate = (date, dataArray) => {
        const valuesForDate = dataArray.filter(row => row[0] === date);
        const sum = valuesForDate.reduce((acc, row) => acc + row[1], null);
        return sum;
    }

    const items = props.getValues && props.getValues(`process[${props.nestIndex}].items`);
    if (!items || !Array.isArray(items)) return <div />;

    return (
        items.map((im, x) => (
            parseInt(im.graph_display) && (props.which === 'none' ? (im.fieldMeasure && im.fieldMeasure.length > 0 || im.labMeasure && im.labMeasure.length > 0 || im.automaticMeasure && im.automaticMeasure.length > 0) :
                    (props.which === 'automatic' ? (im.automaticMeasure && im.automaticMeasure.length > 0) : (props.which === 'lab' ? (im.labMeasure && im.labMeasure.length > 0) : (im.fieldMeasure && im.fieldMeasure.length > 0)))
            ) ?
            <Chart
                key={x}
                width="100%"
                height="480px"
                chartType="LineChart"
                data={formatBegin(im)}
                options={{
                    interpolateNulls: true,
                    responsive: true,
                    tension: 0,
                    tooltip: { isHtml: true },
                    legend: {
                        display: true,
                        position: "top",
                        alignment: "center",
                    },
                    vAxes: {
                        0: {
                            title: im.item_name, titleTextStyle: {
                                italic: false,
                            },
                        }
                    },
                    hAxis: {
                        title: "採取日", titleTextStyle: {
                            italic: false
                        },
                        textStyle: {
                            fontSize: 10
                        }
                    },
                    lang: {
                        noData: 'no data!'
                    },
                    noData: {
                        position: {
                            "x": 0,
                            "y": 0,
                            "align": "center",
                            "verticalAlign": "middle"
                        }
                    },
                    series: seriesColor
                }}
            /> : <div key={x}></div>
        ))
    )
}

export default DrawMeasureChart;
